import { Inbox } from "lucide-react";
import { STATUS_LABELS, STATUS_TONE } from "../../lib/labels";

const TONES = {
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  red: "bg-red-50 text-red-700 border-red-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
};

export const StatusBadge = ({ value, testid }) => (
  <span className={`inline-flex items-center border font-semibold px-2.5 py-0.5 rounded-full text-xs whitespace-nowrap ${TONES[STATUS_TONE[value]] || TONES.slate}`} data-testid={testid}>
    {STATUS_LABELS[value] || value || "-"}
  </span>
);

const LEGAL = {
  NONE: ["Belum Mengajukan Dokumen", "slate"], PENDING: ["Menunggu Pemeriksaan", "amber"],
  REVISION_REQUIRED: ["Perlu Perbaikan", "amber"], VERIFIED: ["Disetujui", "green"], REJECTED: ["Ditolak", "red"],
  UPLOADED: ["Diunggah Pengelola", "blue"], NOT_UPLOADED: ["Belum Diunggah", "slate"],
};

export const LegalBadge = ({ value, testid }) => {
  const [label, tone] = LEGAL[value] || LEGAL.NONE;
  return <span className={`inline-flex items-center border font-semibold px-2.5 py-0.5 rounded-full text-xs whitespace-normal text-left ${TONES[tone]}`} data-testid={testid}>{label}</span>;
};

export const PageHeader = ({ title, subtitle, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
    <div>
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="page-title">{title}</h1>
      {subtitle && <p className="mt-1.5 text-sm text-slate-600 max-w-2xl">{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const StatCard = ({ label, value, icon: Icon, testid, hint }) => (
  <div className="bg-white border border-slate-200/80 rounded-xl p-4 sm:p-6 min-w-0 hover:border-slate-300 transition-colors" data-testid={testid}>
    <div className="flex items-start justify-between">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      {Icon && <Icon className="h-5 w-5 text-emerald-700" />}
    </div>
    <p className="mt-3 text-xl sm:text-2xl xl:text-3xl font-bold text-slate-900 font-mono break-all leading-tight" data-testid={`${testid}-value`}>{value ?? "-"}</p>
    {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
  </div>
);

export const EmptyState = ({ text = "Belum ada data.", testid = "empty-state" }) => (
  <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid={testid}>
    <Inbox className="h-7 w-7 text-slate-400 mx-auto" />
    <p className="mt-3 text-sm text-slate-500">{text}</p>
  </div>
);

const cell = (col, row) => (col.render ? col.render(row) : row[col.key] ?? "-");

export const ResponsiveTable = ({ columns, rows, testid, actions, empty, stackActions = false }) => {
  if (!rows.length) return <EmptyState text={empty} testid={`${testid}-empty`} />;
  return (
    <>
      <div className="hidden md:block overflow-x-auto rounded-xl border border-slate-200/80 bg-white">
        <table className="w-full text-sm" data-testid={testid}>
          <thead className="bg-slate-50 text-left">
            <tr>
              {columns.map((c) => <th key={c.key} className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{c.label}</th>)}
              {actions && <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Aksi</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50/60" data-testid={`${testid}-row-${r.id}`}>
                {columns.map((c) => <td key={c.key} className="px-4 py-3 text-slate-700 align-top">{cell(c, r)}</td>)}
                {actions && <td className={`px-4 py-3 text-right ${stackActions ? "w-48 min-w-[12rem] align-top" : "whitespace-nowrap"}`}>{actions(r, "")}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:hidden space-y-3" data-testid={`${testid}-mobile`}>
        {rows.map((r) => (
          <div key={r.id} className="bg-white border border-slate-200/80 rounded-xl p-4" data-testid={`${testid}-card-${r.id}`}>
            <dl className="space-y-2">
              {columns.map((c) => (
                <div key={c.key} className="flex justify-between gap-4 text-sm">
                  <dt className="text-slate-500 shrink-0">{c.label}</dt>
                  <dd className="text-slate-800 text-right break-words min-w-0">{cell(c, r)}</dd>
                </div>
              ))}
            </dl>
            {actions && <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap justify-end gap-2">{actions(r, "m-")}</div>}
          </div>
        ))}
      </div>
    </>
  );
};
